import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box } from '@node-space/storybook-components/dist/Box'
import { Button, Icon } from '@node-space/ui-components'
import { useIsSuspendedAccount } from 'hooks/useIsSuspendedAccount'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ActionIds } from 'types/types'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { WalletCreateModalContext } from '../../context/WalletCreateModalProvider'
import { WalletAction } from '../Modal/WalletModal'
import { WalletSheet } from '../Sheet/WalletSheet'

interface WalletHeaderButtonsProps {
  setWalletModalAction: React.Dispatch<React.SetStateAction<WalletAction | undefined>>
  canEdit: boolean
  canTrade: boolean
  viewOnly: boolean
  setShowWalletModal?: React.Dispatch<React.SetStateAction<boolean>>
}

const WalletHeaderButtons = ({
  setWalletModalAction,
  canEdit,
  canTrade,
  viewOnly,
  setShowWalletModal,
}: WalletHeaderButtonsProps) => {
  const { t } = useTranslation()
  const { enableUsLaunchRestrictions, enableConvertSheet } = useFeatureFlags()
  const [, setWalletAction] = useContext(WalletActionContext)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [_, setCreateWalletModal] = useContext(WalletCreateModalContext)
  const permissions = useMappedRoles()
  const canCreatePayOut = permissions?.payments?.canCreatePayOut
  const isSuspendedAccount = useIsSuspendedAccount()

  const handleModalAction = (key: string) => {
    setShowWalletModal?.(true)
    setWalletModalAction({ key: key } as unknown as WalletAction)
    track.Amp.track(AmplitudeEvent.WALLET_CONVERT, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.CLICK,
    })
  }

  const triggerOpenSheet = ({
    actionId,
    header,
    onClose,
  }: {
    actionId: ActionIds
    header: string
    onClose?: () => void
  }) => {
    setWalletAction({
      actionId: actionId,
      header: header,
      onClose: () => {
        setIsSheetOpen(false)
        onClose?.()
      },
    })
    setIsSheetOpen(true)
  }

  const handleOpenCreateWalletSheet = () => {
    triggerOpenSheet({
      actionId: ActionIds?.WALLET_CREATE,
      header: t('wallets.addNewWallet'),
    })
  }

  const handleOpenConvertSheet = () => {
    triggerOpenSheet({
      actionId: ActionIds?.CONVERT,
      header: t('wallets.convert.detailsStepHeader'),
    })
  }

  const handleOpenSendSheet = () => {
    triggerOpenSheet({
      actionId: ActionIds?.SEND,
      header: t('wallets.send.contactStepHeader'),
    })
  }

  return (
    <Box flex gap={12}>
      {!viewOnly && canEdit && !isSuspendedAccount && !enableUsLaunchRestrictions && (
        <Button
          testId="buttonAddWallet"
          iconElement={<Icon name="Plus" />}
          onClick={handleOpenCreateWalletSheet}
        >
          {t('wallets.addNewWallet')}
        </Button>
      )}
      {canCreatePayOut && !isSuspendedAccount && (
        <Button
          testId="send-crypto"
          disabled={viewOnly}
          onClick={handleOpenSendSheet}
          iconElement={<Icon name="ArrowUpRight" />}
        >
          {t('payments.sendCrypto')}
        </Button>
      )}
      {!viewOnly && (canEdit || canTrade) && !isSuspendedAccount && !enableUsLaunchRestrictions && (
        <Button
          testId="buttonConvert"
          iconElement={<Icon name="ArrowsLeftRight" />}
          onClick={() =>
            enableConvertSheet ? handleOpenConvertSheet() : handleModalAction('convert')
          }
        >
          {t('walletsPage.convert')}
        </Button>
      )}
      <WalletSheet isSheetOpen={isSheetOpen} />
    </Box>
  )
}

export default React.memo(WalletHeaderButtons)
